/**
 * Validações de CPF/CNPJ, CEP e idade para o assistente de ativação do
 * recebimento automático (T4.3). Espelham, de propósito, as mesmas regras do
 * servidor (`api/src/payment-account/validators.ts`) — barrar aqui evita uma
 * ida ao servidor só para descobrir um dígito verificador errado, mas o
 * servidor valida de novo, então as duas pontas precisam concordar.
 */

export function onlyDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/** CPF com dígito verificador (mod 11), rejeitando sequências repetidas (`00000000000`). */
export function isValidCpf(raw: string | null | undefined): boolean {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;

  const digitAt = (base: string, factorStart: number): number => {
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
      sum += parseInt(base[i], 10) * (factorStart - i);
    }
    const mod = (sum * 10) % 11;
    return mod === 10 ? 0 : mod;
  };

  const d1 = digitAt(cpf.slice(0, 9), 10);
  if (d1 !== parseInt(cpf[9], 10)) return false;
  const d2 = digitAt(cpf.slice(0, 10), 11);
  return d2 === parseInt(cpf[10], 10);
}

/** CNPJ com dígito verificador (pesos 5..2/9..2), rejeitando sequências repetidas. */
export function isValidCnpj(raw: string | null | undefined): boolean {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) return false;

  const digitAt = (base: string): number => {
    const weights =
      base.length === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < base.length; i++) {
      sum += parseInt(base[i], 10) * weights[i];
    }
    const mod = sum % 11;
    return mod < 2 ? 0 : 11 - mod;
  };

  const d1 = digitAt(cnpj.slice(0, 12));
  if (d1 !== parseInt(cnpj[12], 10)) return false;
  const d2 = digitAt(cnpj.slice(0, 13));
  return d2 === parseInt(cnpj[13], 10);
}

/** CEP: 8 dígitos. */
export function isValidCep(raw: string | null | undefined): boolean {
  return onlyDigits(raw).length === 8;
}

/** Agência: só dígitos, 1 a 6 posições. */
export function isValidBankAgency(raw: string | null | undefined): boolean {
  const digits = onlyDigits(raw);
  return digits.length >= 1 && digits.length <= 6;
}

/** Número da conta: só dígitos, 1 a 20 posições. */
export function isValidBankAccount(raw: string | null | undefined): boolean {
  const digits = onlyDigits(raw);
  return digits.length >= 1 && digits.length <= 20;
}

/** Dígito verificador da conta: opcional; quando existe, um único caractere alfanumérico. */
export function isValidBankAccountDigit(raw: string | null | undefined): boolean {
  if (raw == null || raw === '') return true;
  return /^[0-9Xx]$/.test(raw.trim());
}

/**
 * 18+ a partir de `dd/MM/yyyy` (o que o campo mascarado guarda), contada em
 * UTC para não depender do fuso do aparelho.
 */
export function isAdultBrDate(brDate: string | null | undefined, minAge = 18): boolean {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((brDate ?? '').trim());
  if (!match) return false;
  const [, dayStr, monthStr, yearStr] = match;
  const day = Number(dayStr);
  const month = Number(monthStr);
  const year = Number(yearStr);

  const birth = new Date(Date.UTC(year, month - 1, day));
  if (
    birth.getUTCFullYear() !== year ||
    birth.getUTCMonth() !== month - 1 ||
    birth.getUTCDate() !== day
  ) {
    return false;
  }
  const now = new Date();
  if (birth.getTime() > now.getTime()) return false;

  const cutoff = new Date(
    Date.UTC(now.getUTCFullYear() - minAge, now.getUTCMonth(), now.getUTCDate()),
  );
  return birth.getTime() <= cutoff.getTime();
}

/** `dd/MM/yyyy` → `yyyy-MM-dd` (o que o `CreatePaymentAccountDto.birthDate` espera). */
export function brDateToIso(brDate: string): string {
  const [day, month, year] = brDate.split('/');
  return `${year}-${month}-${day}`;
}
